import SideModal from "components/SideModal";
import { useTranslation } from "contexts/i18nContext";
import { DateView } from "utils/helper";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  evaluations: any;
}

export default function ViewEvaluationModal({
  isOpen,
  setIsOpen,
  evaluations,
}: Props) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("prosecutorDecisionRegistry")}
    >
      <div className="space-y-6">
        {/* Decision Details Section */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-5 w-1 bg-red-600 rounded-full" />
            <h3 className="text-lg font-semibold text-gray-900">
              {t("decisionDetails")}
            </h3>
          </div>

          <div className="space-y-6">
            {Array.isArray(evaluations) && evaluations.length > 0 ? (
              evaluations.map((evalItem: any, idx: number) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("evaluation")} #{idx + 1}
                    </h4>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Evaluation Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        {t("evaluationDate")}
                      </label>
                      <div className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700">
                        {DateView(evalItem.evaluation_date) || "-"}
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          {t("workSpeedEvaluation")}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={evalItem.work_speed_evaluation || ""}
                            readOnly
                            className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 pr-10 focus:outline-none cursor-default"
                          />
                          <span className="absolute right-3 top-2.5 text-gray-400 text-sm">
                            /100
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          {t("workQualityEvaluation")}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={evalItem.work_quality_evaluation || ""}
                            readOnly
                            className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 pr-10 focus:outline-none cursor-default"
                          />
                          <span className="absolute right-3 top-2.5 text-gray-400 text-sm">
                            /100
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          {t("workLoadEvaluation")}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={evalItem.work_load_evaluation || ""}
                            readOnly
                            className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 pr-10 focus:outline-none cursor-default"
                          />
                          <span className="absolute right-3 top-2.5 text-gray-400 text-sm">
                            /100
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Assigned Prosecutor (if present) */}
                    {evalItem.assigned_prosecutor && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                          {t("assignedProsecutor")}
                        </label>
                        <input
                          type="text"
                          value={evalItem.assigned_prosecutor}
                          readOnly
                          className="w-full bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none cursor-default"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500 text-sm">
                {t("noEvaluationsFound")}
              </div>
            )}
          </div>
        </section>

        {/* Action Button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            {t("close")}
          </button>
        </div>
      </div>
    </SideModal>
  );
}
